/**
 * `LagDetailModal` — the click-to-open drill-down for a single freshness lag
 * signal (a top indexer-lag card or a `(freshness)` endpoint row). Toggles
 * between the page's bar view (relative scale, zoomed in) and a line graph
 * (absolute seconds axis), and lets the operator switch the time window to
 * inspect a dip across 1h / 24h / 90d — "более подробной инфой по падению".
 *
 * Self-fetches via the shared uptime query key so it dedupes against the page's
 * existing data (no extra request for the window already on screen) and picks
 * the matching endpoint out of the window payload by `endpointKey`.
 */
import clsx from 'clsx';
import { useQuery } from '@tanstack/react-query';
import { useRef, useState } from 'react';
import { parseEndpointKey, stripFreshnessSuffix } from '../lib/endpoint-key';
import {
  lagToTone,
  pipelineFromEndpointKey,
  type LagTone,
  type Pipeline,
} from '../lib/lag-bars';
import { expectedBucketCount } from '../lib/lag-pad';
import type { LagWindow } from '../lib/lag-time';
import {
  fetchUptimeWindow,
  uptimeQueryKey,
  type UptimeLagEndpoint,
} from '../lib/uptime-api';
import type { LagView } from '../lib/url-state';
import { formatLag } from './IndexerPipelineCard';
import { LagHistoryBar } from './LagHistoryBar';
import { LagLineChart } from './LagLineChart';
import { ModalCloseButton, ModalShell } from './ModalShell';
import { SegmentedControl } from './SegmentedControl';

type ChartView = LagView;

const TONE_DOT: Record<LagTone, string> = {
  ok: 'bg-ok shadow-[0_0_0_3px_rgba(26,166,107,.18)]',
  warn: 'bg-warn shadow-[0_0_0_3px_rgba(224,160,25,.2)]',
  down: 'bg-down shadow-[0_0_0_3px_rgba(227,80,62,.2)]',
  none: 'bg-line-strong',
};

const VIEW_OPTIONS = [
  { value: 'bar' as const, label: 'Bar' },
  { value: 'graph' as const, label: 'Graph' },
];

const WINDOW_OPTIONS = [
  { value: '1h' as const, label: '1h' },
  { value: '24h' as const, label: '24h' },
  { value: '90d' as const, label: '90d' },
];

export interface LagDetailModalProps {
  componentId: number;
  endpointKey: string;
  /** Heading text (e.g. "API v3 indexer lag" or the service display name). */
  title: string;
  network: 'mainnet' | 'testnet';
  /** Defaults to `pipelineFromEndpointKey(endpointKey)` when omitted. */
  pipeline?: Pipeline;
  defaultWindow: LagWindow;
  /** Initial chart view — deep links may open straight into the graph. */
  defaultView?: ChartView;
  /** Notify on toggles so the opener can mirror them into the URL. */
  onViewChange?: (view: ChartView) => void;
  onWindowChange?: (window: LagWindow) => void;
  onClose: () => void;
}

export function LagDetailModal({
  componentId,
  endpointKey,
  title,
  network,
  pipeline: pipelineProp,
  defaultWindow,
  defaultView = 'bar',
  onViewChange,
  onWindowChange,
  onClose,
}: LagDetailModalProps) {
  const closeBtnRef = useRef<HTMLButtonElement>(null);
  const [view, setView] = useState<ChartView>(defaultView);
  const [windowSel, setWindowSel] = useState<LagWindow>(defaultWindow);

  const query = useQuery({
    queryKey: uptimeQueryKey(componentId, windowSel),
    queryFn: () => fetchUptimeWindow(componentId, windowSel),
    refetchInterval: windowSel === '1h' ? 30_000 : 60_000,
    staleTime: windowSel === '1h' ? 15_000 : 30_000,
  });

  const pipeline = pipelineProp ?? pipelineFromEndpointKey(endpointKey);
  const ep: UptimeLagEndpoint | null =
    query.data?.endpoints.find((e) => e.endpointKey === endpointKey) ?? null;
  const buckets = ep?.lagBuckets ?? [];
  const tone = lagToTone(ep?.currentLagSec ?? null, pipeline);

  const parsed = parseEndpointKey(endpointKey);
  const displayPath = stripFreshnessSuffix(parsed.path);
  const subtitle = `${parsed.method ? `${parsed.method} ` : ''}${displayPath} · ${network}`;
  const summary = `now ${formatLag(ep?.currentLagSec)} · p95 ${formatLag(ep?.p95LagSec)} · max ${formatLag(ep?.maxLagSec)}`;

  const loading = query.isLoading || (!query.data && query.isFetching);

  return (
    <ModalShell
      onClose={onClose}
      labelledById="lag-detail-title"
      maxWidthClass="max-w-xl"
      initialFocusRef={closeBtnRef}
    >
      <header className="flex items-start gap-3 border-b border-line px-5 py-4">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span
              aria-hidden
              className={clsx(
                'inline-block h-2 w-2 shrink-0 rounded-full',
                TONE_DOT[tone],
              )}
            />
            <h2
              id="lag-detail-title"
              className="m-0 text-[15px] font-semibold tracking-[-.01em] text-ink-1"
            >
              {title}
            </h2>
          </div>
          <p className="m-0 mt-1 truncate font-mono text-[11.5px] tracking-[-.01em] text-ink-3">
            {subtitle}
          </p>
          <p className="m-0 mt-1.5 font-mono text-[12px] text-ink-2 [font-feature-settings:'tnum']">
            {summary}
          </p>
        </div>
        <ModalCloseButton onClose={onClose} buttonRef={closeBtnRef} />
      </header>

      <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-5 py-4">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <SegmentedControl
            value={view}
            onChange={(next) => {
              setView(next);
              onViewChange?.(next);
            }}
            options={VIEW_OPTIONS}
            layoutId="lagdetail-view-active"
            ariaLabel="Chart view"
          />
          <SegmentedControl
            value={windowSel}
            onChange={(next) => {
              setWindowSel(next);
              onWindowChange?.(next);
            }}
            options={WINDOW_OPTIONS}
            layoutId="lagdetail-window-active"
            ariaLabel="Time window"
          />
        </div>

        <div className="min-h-[200px]">
          {loading ? (
            <div
              className="skeleton-shine w-full rounded-md"
              style={{ height: 200 }}
            />
          ) : buckets.length > 0 ? (
            view === 'bar' ? (
              <LagHistoryBar
                buckets={buckets}
                pipeline={pipeline}
                windowSel={windowSel}
                expectedCount={expectedBucketCount(windowSel)}
                endpointKey={endpointKey}
                heightClass="h-44"
              />
            ) : (
              <LagLineChart
                buckets={buckets}
                pipeline={pipeline}
                windowSel={windowSel}
                endpointKey={endpointKey}
              />
            )
          ) : (
            <div className="flex h-[200px] items-center justify-center text-[13px] text-ink-3">
              No lag history for this window.
            </div>
          )}
        </div>
      </div>
    </ModalShell>
  );
}
